"use client";

import { useMemo, memo, useCallback } from "react";
import Link from "next/link";
import { ChevronDown, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/images/logo";
import { routes } from "@/config/routes";
import { MemoizedFilterHeader as FilterHeader } from "./filter-header";
import type { BlockConfig } from "@/lib/registry/types";
// Import FilterControls and its types
import { FilterControls, type SortOptionValue } from "./filter-controls";
// Add motion and Button imports
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

// Define item type matching the one used elsewhere
// Removed local Item type definition
// Define Item type using BlockConfig and id
// type Item = BlockConfig & { id: string };

interface ComponentBrowserHeaderProps {
  // Filter state & handlers (likely from useComponentFilters)
  libraries: string[]; // All available libraries
  categories: string[]; // All available categories
  activeLibraries: string[];
  activeCategories: string[];
  onLibraryToggle: (library: string) => void;
  onCategoryToggle: (category: string) => void;
  onShowOnlyLibrary: (library: string) => void;
  onShowOnlyCategory: (category: string) => void;
  onResetLibraries: () => void;
  onResetCategories: () => void;
  onResetAllFilters: () => void;
  availableLibraries: string[]; // Passed to FilterHeader
  availableCategories: string[]; // Passed to FilterHeader
  // Use BlockConfig for components
  components: (BlockConfig & { id: string })[]; // Passed to FilterHeader (or subset/count)

  // Scroll/collapse state & handlers (from useScrollCollapse)
  isPageScrolled: boolean;
  isFullyCollapsed: boolean;
  toggleFullyCollapsed: () => void;

  // Demo toggle state & handler
  showDemos: boolean;
  onToggleDemos: (checked: boolean) => void;

  // --- ADD props for FilterControls --- //
  searchQuery: string;
  onSearchChange: (value: string) => void;
  showStarredOnly: boolean;
  onToggleStarredOnly: (checked: boolean) => void;
  starredItemsCount: number;
  sortOption: SortOptionValue;
  onSortChange: (value: SortOptionValue) => void;
  // --- End FilterControls props ---
}

export const ComponentBrowserHeader = ({
  libraries,
  categories,
  activeLibraries,
  activeCategories,
  onLibraryToggle,
  onCategoryToggle,
  onShowOnlyLibrary,
  onShowOnlyCategory,
  onResetLibraries,
  onResetCategories,
  onResetAllFilters,
  availableLibraries,
  availableCategories,
  components,
  isPageScrolled,
  isFullyCollapsed,
  toggleFullyCollapsed,
  showDemos,
  onToggleDemos,
  searchQuery,
  onSearchChange,
  showStarredOnly,
  onToggleStarredOnly,
  starredItemsCount,
  sortOption,
  onSortChange,
}: ComponentBrowserHeaderProps) => {
  // Memoized Collapse Button Element
  const collapseButton = useMemo(
    () => (
      <div
        className={cn(
          "fixed mt-[1px] top-2 right-2 z-[60] transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
          isPageScrolled
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-10 pointer-events-none"
        )}
        style={{ transitionDuration: "400ms" }} // Moved duration here
      >
        <button
          onClick={toggleFullyCollapsed}
          className="size-5 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center transition-transform duration-300 hover:scale-105 active:scale-95"
          aria-label={isFullyCollapsed ? "Expand header" : "Collapse header"}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
              isFullyCollapsed ? "rotate-0" : "rotate-180" // Simpler rotation logic
            )}
            style={{ transitionDuration: "400ms" }}
          />
        </button>
      </div>
    ),
    [isPageScrolled, isFullyCollapsed, toggleFullyCollapsed]
  );

  // Memoized Logo Element
  const logoComponent = useMemo(
    () => (
      <div
        className={cn(
          "fixed left-4 top-1 z-[60] flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
          isPageScrolled
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-10 pointer-events-none"
        )}
        style={{ transitionDuration: "400ms" }}
      >
        <Link
          href={routes.home}
          className={cn(
            "hover:opacity-80 transition-opacity duration-500 delay-500 backdrop-blur-lg rounded-full p-1",
            isFullyCollapsed ? "bg-background/80" : ""
          )}
        >
          <Logo className="h-6 w-6 text-primary" />
        </Link>
      </div>
    ),
    [isPageScrolled, isFullyCollapsed] // Only depends on scroll/collapse state
  );

  // Create a memoized element for FilterControls and the Demo button
  const controlsElement = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {" "}
        {/* Adjust gap as needed */}
        {/* Demo Toggle Button */}
        <motion.div whileTap={{ scale: 0.9, rotate: -15 }}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleDemos(!showDemos)}
            aria-pressed={showDemos}
            aria-label={showDemos ? "Hide demos" : "Show demos"}
            className={cn(
              "rounded-full transition-colors duration-300 h-7 w-7", // Match FilterControls size
              isPageScrolled && "scale-90", // Apply scale when scrolled
              showDemos
                ? "text-yellow-500 hover:bg-yellow-500/10 hover:text-yellow-600"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <Zap
              className={cn(
                "h-4 w-4 transition-all duration-300",
                isPageScrolled && "h-3.5 w-3.5", // Smaller icon when scrolled
                showDemos ? "fill-current" : ""
              )}
            />
          </Button>
        </motion.div>
        {/* Filter Controls Component */}
        <FilterControls
          isScrolled={isPageScrolled}
          searchTerm={searchQuery}
          sortOption={sortOption}
          showStarredOnly={showStarredOnly}
          starredItemsCount={starredItemsCount}
          onSearchChange={onSearchChange}
          onSortChange={onSortChange}
          onToggleStarredOnly={onToggleStarredOnly}
        />
      </div>
    ),
    [
      // Dependencies for the controls element
      isPageScrolled,
      searchQuery,
      sortOption,
      showStarredOnly,
      starredItemsCount,
      onSearchChange, // Assumed stable
      onSortChange, // Assumed stable
      onToggleStarredOnly, // Assumed stable
      onToggleDemos, // Assumed stable
      showDemos,
    ]
  );

  return (
    <>
      {/* Render collapse button and logo (positioned fixed) */}
      {collapseButton}
      {logoComponent}

      {/* Sticky container for the header content, manages height transition */}
      <div
        className="sticky top-0 z-50 w-full bg-transparent pointer-events-none" // Made transparent and pointer-events-none to only manage space
        style={{
          minHeight: isPageScrolled
            ? isFullyCollapsed
              ? "0px"
              : "73px" // Target height when expanded and scrolled
            : "73px", // Target height when not scrolled
          transition: "min-height 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {/* Actual header content with visibility & transform transitions */}
        <div
          className={cn(
            "absolute inset-x-0 top-0 z-10 bg-background/80 backdrop-blur-lg py-2 transition-all duration-300 w-full overflow-hidden pointer-events-auto", // Re-enable pointer events here
            isPageScrolled ? "shadow-sm" : "",
            isFullyCollapsed && isPageScrolled
              ? "opacity-0 translate-y-[-105%]"
              : "opacity-100 translate-y-0"
          )}
          style={{
            transitionProperty: "opacity, transform",
            transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            transitionDuration: "400ms",
          }}
          aria-hidden={isFullyCollapsed && isPageScrolled} // Hide from assistive tech when collapsed
        >
          <div
            className={cn(
              "container transition-all duration-300",
              isPageScrolled ? "pl-12 pr-8 max-w-none" : "pl-6 pr-6" // Adjust padding based on scroll state and logo/button presence
            )}
            style={{
              transitionProperty: "padding",
              transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
              transitionDuration: "400ms",
            }}
          >
            {/* Filter Header Component with integrated Filter Controls */}
            <FilterHeader
              isScrolled={isPageScrolled}
              libraries={libraries}
              categories={categories}
              activeLibraries={activeLibraries}
              activeCategories={activeCategories}
              availableLibraries={availableLibraries}
              availableCategories={availableCategories}
              exclusiveMode="none" // Assuming default, adjust if needed
              onLibraryToggle={onLibraryToggle}
              onCategoryToggle={onCategoryToggle}
              onShowOnlyLibrary={onShowOnlyLibrary}
              onShowOnlyCategory={onShowOnlyCategory}
              onResetLibraries={onResetLibraries}
              onResetCategories={onResetCategories}
              onResetAllFilters={onResetAllFilters}
              components={components} // Pass components for search/count
              showDemos={showDemos}
              onToggleDemos={onToggleDemos}
            >
              {/* Pass the memoized controls element as children */}
              {controlsElement}
            </FilterHeader>
          </div>
        </div>
      </div>
    </>
  );
};

// Memoize the component
export const MemoizedComponentBrowserHeader = memo(ComponentBrowserHeader);
// Ensure the display name is preserved if needed elsewhere, though memo usually handles this
MemoizedComponentBrowserHeader.displayName = "ComponentBrowserHeader";
